import React, { useState } from 'react';
import { Box, Container, Typography, Button, Stack, TextField, Fade, Alert, MenuItem, Grid, Link } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import { submitForm } from '../utils/formSubmission';

export default function ContactPage() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    enquiryFor: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitted(false);
    const formData = {
      ...form,
      submitted_at: new Date().toISOString(),
    };
    await submitForm(formData, {
      formName: 'Contact Form',
      formSource: 'website_contact_page',
      onLoading: setSubmitting,
      onSuccess: () => {
        setSubmitted(true);
        setForm({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          enquiryFor: '',
          message: '',
        });
      },
      onError: (error) => {
        setError(error.message || 'Failed to submit form. Please try again.');
      },
    });
  };

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Grid container spacing={6} alignItems="flex-start" justifyContent="center">
            {/* Left column: Info */}
            <Grid item xs={12} md={5}>
              <Box sx={{ pr: { md: 6 }, mb: { xs: 4, md: 0 } }}>
                <Typography variant="h2" fontWeight={900} sx={{ fontSize: { xs: 32, md: 48 }, mb: 3, lineHeight: 1.1 }}>
                  have a<br />question?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', fontSize: { xs: 16, md: 18 } }}>
                  We're here to help! Fill out the form or email us, and our team will ensure you have the best experience—whether you have a question about your order or need expert advice.
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontSize: { xs: 15, md: 16 } }}>
                  We respond personally to every inquiry. Please allow up to 24 hours for a reply during business hours (Monday to Friday, 9am–5pm EST).
                </Typography>
                <Stack spacing={2} sx={{ mt: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EmailIcon sx={{ fontSize: 22, color: '#0B60B1' }} />
                    <Link href="mailto:hello@impactquill.com" underline="hover" sx={{ fontSize: 16, color: 'inherit' }}>
                      hello@impactquill.com
                    </Link>
                  </Stack>
                </Stack>
              </Box>
            </Grid>
            {/* Right column: Form */}
            <Grid item xs={12} md={7}>
              <Box sx={{ width: '100%' }}>
                <form onSubmit={handleSubmit} autoComplete="off">
                  <Stack spacing={2.5}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="*First Name"
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        required
                        fullWidth
                        variant="outlined"
                        size="medium"
                        inputProps={{ style: { fontSize: 16 } }}
                      />
                      <TextField
                        label="*Last Name"
                        name="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        required
                        fullWidth
                        variant="outlined"
                        size="medium"
                        inputProps={{ style: { fontSize: 16 } }}
                      />
                    </Stack>
                    <TextField
                      label="*Email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      fullWidth
                      variant="outlined"
                      type="email"
                      size="medium"
                      inputProps={{ style: { fontSize: 16 } }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: '#0B60B1',
                          },
                        },
                        '& label.Mui-focused': {
                          color: '#0B60B1',
                        },
                      }}
                    />
                    <TextField
                      label="Phone Number (optional)"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      fullWidth
                      variant="outlined"
                      type="tel"
                      size="medium"
                      inputProps={{ style: { fontSize: 16 } }}
                    />
                    <TextField
                      select
                      label="*Enquiry for"
                      name="enquiryFor"
                      value={form.enquiryFor}
                      onChange={handleChange}
                      required
                      fullWidth
                      variant="outlined"
                      size="medium"
                    >
                      <MenuItem value="General">General</MenuItem>
                      <MenuItem value="Consulting">Consulting</MenuItem>
                      <MenuItem value="Support">Support</MenuItem>
                      <MenuItem value="Partnership">Partnership</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                    <TextField
                      label="*Message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={5}
                      maxRows={10}
                      size="medium"
                      inputProps={{ style: { fontSize: 16 } }}
                    />
                    {error && <Alert severity="error">{error}</Alert>}
                    {submitted && (
                      <Alert severity="success" sx={{ fontWeight: 600, fontSize: 16 }}>
                        Thank you! We've received your message.
                      </Alert>
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      endIcon={<SendIcon />}
                      disabled={submitting}
                      sx={{
                        fontWeight: 700,
                        py: 1.5,
                        fontSize: 18,
                        borderRadius: 0,
                        background: '#0B60B1',
                        color: '#fff',
                        boxShadow: 0,
                        mt: 1,
                        textTransform: 'none',
                        letterSpacing: 0,
                        '&:hover': {
                          background: '#1976d2',
                        },
                      }}
                      fullWidth
                    >
                      {submitting ? 'Sending...' : 'SUBMIT'}
                    </Button>
                  </Stack>
                </form>
              </Box>
            </Grid>
          </Grid>
        </Fade>
      </Container>
    </Box>
  );
} 